import {AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2, inject} from '@angular/core';
import {LanguageService} from '../services/language.service';

@Directive({
  selector: '[appVideoSourceError]',
  standalone: true
})
export class VideoSourceErrorDirective implements AfterViewInit, OnDestroy {
  private el: ElementRef<HTMLVideoElement> = inject(ElementRef<HTMLVideoElement>);
  private renderer: Renderer2 = inject(Renderer2);
  private languageService: LanguageService = inject(LanguageService);

  @Input() public videoLoadErrorTextKey: string = 'VIDEOLOADERROR';

  private overlay: HTMLElement | null = null;
  private overlayText: HTMLElement | null = null;
  private parentElement: HTMLElement | null = null;
  private previousParentPosition: string | null = null;
  private previousVideoVisibility: string | null = null;
  private mutationObserver: MutationObserver | null = null;
  private currentSrc: string | null | undefined = undefined;
  private readonly updateOverlayPosition: () => void = () => {
    this.positionOverlay();
  };
  private readonly capturedErrorListener: () => void = () => {
    this.showOverlay();
  };

  @Input()
  public set src(value: string | null | undefined) {
    if (this.currentSrc !== value) {
      this.currentSrc = value;
      this.removeOverlay();
    }
  }

  public ngAfterViewInit(): void {
    const video = this.el.nativeElement;

    video.addEventListener('error', this.capturedErrorListener, true);
    this.mutationObserver = new MutationObserver(() => {
      this.removeOverlay();
    });
    this.mutationObserver.observe(video, {
      attributeFilter: ['src'],
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  @HostListener('error')
  public onVideoError(): void {
    this.showOverlay();
  }

  @HostListener('loadeddata')
  @HostListener('canplay')
  public onVideoLoaded(): void {
    this.removeOverlay();
  }

  public ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('error', this.capturedErrorListener, true);
    this.mutationObserver?.disconnect();
    this.removeOverlay();
  }

  private showOverlay(): void {
    const video = this.el.nativeElement;
    const parent = video.parentElement;

    if (!parent) {
      return;
    }

    this.parentElement = parent;
    this.ensurePositionedParent(parent);
    this.hideVideo(video);

    if (!this.overlay) {
      this.overlay = this.createOverlay();
      this.renderer.appendChild(parent, this.overlay);
      window.addEventListener('resize', this.updateOverlayPosition);
    }

    this.updateText();
    this.positionOverlay();
  }

  private createOverlay(): HTMLElement {
    const overlay = this.renderer.createElement('div') as HTMLElement;
    const icon = this.renderer.createElement('span') as HTMLElement;
    const text = this.renderer.createElement('span') as HTMLElement;

    this.renderer.addClass(overlay, 'video-source-error-overlay');
    this.renderer.addClass(icon, 'mts-icon');
    this.renderer.addClass(icon, 'video-source-error-icon');
    this.renderer.addClass(text, 'video-source-error-text');

    this.renderer.setAttribute(overlay, 'role', 'status');
    this.renderer.setStyle(overlay, 'position', 'absolute');
    this.renderer.setStyle(overlay, 'z-index', '10');
    this.renderer.setStyle(overlay, 'display', 'flex');
    this.renderer.setStyle(overlay, 'flex-direction', 'column');
    this.renderer.setStyle(overlay, 'align-items', 'center');
    this.renderer.setStyle(overlay, 'justify-content', 'center');
    this.renderer.setStyle(overlay, 'gap', '0.25rem');
    this.renderer.setStyle(overlay, 'padding', '0.5rem');
    this.renderer.setStyle(overlay, 'background', 'var(--bs-body-bg, #fff)');
    this.renderer.setStyle(overlay, 'color', 'var(--bs-secondary-color, #6c757d)');
    this.renderer.setStyle(overlay, 'border', '1px solid var(--bs-border-color, #dee2e6)');
    this.renderer.setStyle(overlay, 'border-radius', '0.25rem');
    this.renderer.setStyle(overlay, 'text-align', 'center');
    this.renderer.setStyle(overlay, 'line-height', '1.2');
    this.renderer.setStyle(overlay, 'pointer-events', 'none');
    this.renderer.setStyle(overlay, 'box-sizing', 'border-box');

    this.renderer.setStyle(icon, 'font-size', '2rem');
    this.renderer.setStyle(icon, 'line-height', '1');
    this.renderer.setStyle(text, 'font-size', '0.875rem');
    this.renderer.setStyle(text, 'font-weight', '600');
    this.renderer.setStyle(text, 'overflow-wrap', 'anywhere');

    this.renderer.setProperty(icon, 'textContent', 'videocam_off');
    this.renderer.appendChild(overlay, icon);
    this.renderer.appendChild(overlay, text);

    this.overlayText = text;
    return overlay;
  }

  private ensurePositionedParent(parent: HTMLElement): void {
    const position = window.getComputedStyle(parent).position;

    if (position === 'static') {
      this.previousParentPosition = parent.style.position;
      this.renderer.setStyle(parent, 'position', 'relative');
    }
  }

  private hideVideo(video: HTMLVideoElement): void {
    if (this.previousVideoVisibility === null) {
      this.previousVideoVisibility = video.style.visibility;
    }

    this.renderer.setStyle(video, 'visibility', 'hidden');
  }

  private positionOverlay(): void {
    if (!this.overlay) {
      return;
    }

    const video = this.el.nativeElement;
    this.renderer.setStyle(this.overlay, 'top', `${video.offsetTop}px`);
    this.renderer.setStyle(this.overlay, 'left', `${video.offsetLeft}px`);
    this.renderer.setStyle(this.overlay, 'width', `${video.offsetWidth}px`);
    this.renderer.setStyle(this.overlay, 'height', `${video.offsetHeight}px`);
  }

  private updateText(): void {
    if (!this.overlayText) {
      return;
    }

    this.renderer.setProperty(
      this.overlayText,
      'textContent',
      this.languageService.getTranslationByKey(this.videoLoadErrorTextKey)
    );
  }

  private removeOverlay(): void {
    if (this.overlay && this.parentElement) {
      this.renderer.removeChild(this.parentElement, this.overlay);
    }

    if (this.parentElement && this.previousParentPosition !== null) {
      this.renderer.setStyle(this.parentElement, 'position', this.previousParentPosition);
    }

    if (this.previousVideoVisibility !== null) {
      this.renderer.setStyle(this.el.nativeElement, 'visibility', this.previousVideoVisibility);
    }

    window.removeEventListener('resize', this.updateOverlayPosition);

    this.overlay = null;
    this.overlayText = null;
    this.parentElement = null;
    this.previousParentPosition = null;
    this.previousVideoVisibility = null;
  }
}
